import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  authService,
  clinicService,
  doctorService,
  getLocalizedValue,
  hospitalService,
  normalizeImageValue,
  specialtyService,
} from '@/services/api';
import {
  BellRing,
  Briefcase,
  Building2,
  Camera,
  ChevronLeft,
  Eye,
  EyeOff,
  HeartHandshake,
  Loader2,
  Lock,
  Mail,
  MapPin,
  Star,
  Phone,
  Save,
  ShieldCheck,
  User,
} from 'lucide-react';

const NONE_VALUE = '__none__';

const EMPTY_PROFILE = {
  id: null,
  accountName: '',
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  mobile: '',
  licenseNumber: '',
  specialtyIds: [],
  specialtyLabels: [],
  experience: '',
  bio: '',
  consultationFee: '',
  discountPrice: '',
  doctorCurrency: 'SYP',
  priority: '',
  waitingAppointments: '',
  featured: false,
  enableAppointment: true,
  acceptAppointment: false,
  allowPatientMultipleBookingsSameDay: false,
  enableWaitingAppointments: false,
  waitingAppointmentDelay: '',
  enableOnlineConsultation: false,
  enableAtClinic: true,
  enableAtCustomerAddress: false,
  homeVisitFee: '',
  homeVisitDiscountFee: '',
  sessionDuration: '',
  appointmentDelay: '',
  scheduledAppointmentPrefix: '',
  waitingAppointmentPrefix: '',
  showQueueNumberToPatient: false,
  showRemainingTurnsToPatient: false,
  enableTurnApproachingReminder: false,
  reminderWhenRemainingTurns: '',
  showEstimatedTurnTimeToPatient: false,
  reminderBeforeMinutes: '',
  reminderPatientBeforeMinutes: '',
  commissionRate: '',
  averageRating: 0,
  totalReviews: 0,
  clinicId: '',
  hospitalId: '',
  clinicAddress: '',
  image: '',
};

const splitDisplayName = (fullName = '') => {
  const parts = String(fullName).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return { firstName: '', lastName: '' };
  }

  if (parts.length === 1) {
    return { firstName: parts[0], lastName: '' };
  }

  return {
    firstName: parts.slice(0, -1).join(' '),
    lastName: parts.at(-1) || '',
  };
};

const readString = (value) => {
  if (value == null || value === false) return '';
  if (typeof value === 'object' && !Array.isArray(value)) {
    for (const key of ['name', 'display_name', 'value', 'login', 'ar', 'en']) {
      const resolved = readString(value[key]);
      if (resolved) return resolved;
    }
    return '';
  }

  return String(value).trim();
};

const joinNameParts = (...parts) =>
  parts
    .map((part) => readString(part))
    .filter(Boolean)
    .join(' ')
    .trim();

const stripHtml = (value) =>
  readString(value)
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>|<\/div>|<\/li>/gi, '\n')
    .replace(/<li[^>]*>/gi, '- ')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .replace(/[ \t]{2,}/g, ' ')
    .trim();

const readList = (value) => (Array.isArray(value) ? value : []);

const readId = (value) => {
  if (value == null || value === false || value === '') return '';
  if (typeof value === 'object' && !Array.isArray(value)) {
    if (value.id != null && value.id !== false) return String(value.id);
    return '';
  }
  return String(value);
};

const normalizeIdList = (value) =>
  readList(value)
    .map((item) => {
      if (typeof item === 'object' && !Array.isArray(item)) {
        return readId(item.id);
      }
      return readId(item);
    })
    .filter(Boolean);

const firstArray = (...candidates) => candidates.find((item) => Array.isArray(item)) || [];

const toOptionValue = (value) => value || NONE_VALUE;

const resolveDoctorProfileImage = (doctor = {}) => {
  const media = Array.isArray(doctor.media) ? doctor.media[0] : doctor.media;
  const candidates = [
    doctor.image,
    doctor.image_url,
    doctor.avatar,
    doctor.profile_image,
    doctor.profilePicture,
    doctor.photo,
    doctor.image?.url,
    doctor.image?.thumb,
    doctor.avatar?.url,
    media?.url,
    media?.thumb,
    media?.icon,
  ];

  for (const candidate of candidates) {
    const image = normalizeImageValue(readString(candidate));
    if (image) return image;
  }

  return '';
};

const mapDoctorToProfile = (doctor, lang, fallbackUser = null) => {
  const fullName = readString(doctor.name);
  const fallbackName = splitDisplayName(fullName);
  const fallbackAccountName =
    readString(fallbackUser?.name) ||
    joinNameParts(fallbackUser?.first_name, fallbackUser?.last_name);
  const specialtyRows = firstArray(
    readList(doctor.specialities),
    doctor.specialty ? [doctor.specialty] : [],
    doctor.speciality ? [doctor.speciality] : []
  );
  const specialtyIds = normalizeIdList(doctor.speciality_ids);
  const selectedSpecialtyIds = specialtyIds.length
    ? specialtyIds
    : specialtyRows.map((item) => readId(item?.id || item)).filter(Boolean);
  const specialtyLabels = specialtyRows
    .map((item) => getLocalizedValue(item?.name || item, lang) || readString(item?.name || item))
    .filter(Boolean);

  return {
    id: doctor.id || fallbackUser?.id || null,
    accountName:
      fallbackAccountName ||
      readString(doctor.linked_user_name) ||
      readString(doctor.linked_user_login),
    firstName: readString(doctor.first_name) || fallbackName.firstName,
    lastName: readString(doctor.last_name) || fallbackName.lastName,
    email: readString(doctor.email),
    phone: readString(doctor.phone) || readString(doctor.phone_number),
    mobile:
      readString(doctor.mobile) ||
      readString(doctor.mobile_number) ||
      readString(doctor.phone) ||
      readString(doctor.phone_number),
    licenseNumber: readString(doctor.license_number),
    specialtyIds: selectedSpecialtyIds,
    specialtyLabels,
    experience: String(doctor.experience_years ?? doctor.experience ?? ''),
    bio: stripHtml(doctor.bio) || stripHtml(doctor.description) || stripHtml(doctor.about) || stripHtml(doctor.short_bio),
    consultationFee: String(
      doctor.consultation_price ?? doctor.consultation_fee ?? doctor.price ?? ''
    ),
    discountPrice: String(doctor.discount_price ?? ''),
    doctorCurrency: readString(doctor.doctor_currency) || readString(doctor.currency) || 'SYP',
    priority: String(doctor.priority ?? ''),
    waitingAppointments: String(
      doctor.waiting_appointments_limit ?? doctor.waiting_appointments ?? ''
    ),
    featured: !!doctor.featured,
    enableAppointment: doctor.enable_appointment ?? true,
    acceptAppointment: !!doctor.accept_appointment,
    allowPatientMultipleBookingsSameDay: !!doctor.allow_patient_multiple_bookings_same_day,
    enableWaitingAppointments: !!doctor.enable_waiting_appointments,
    waitingAppointmentDelay: String(doctor.waiting_appointment_delay ?? ''),
    enableOnlineConsultation: !!doctor.enable_online_consultation,
    enableAtClinic: doctor.enable_at_clinic ?? true,
    enableAtCustomerAddress: !!doctor.enable_at_customer_address,
    homeVisitFee: String(doctor.home_visit_fee || ''),
    homeVisitDiscountFee: String(doctor.home_visit_discount_fee || ''),
    sessionDuration: String(doctor.session_duration ?? ''),
    appointmentDelay: String(doctor.appointment_delay ?? ''),
    scheduledAppointmentPrefix: readString(doctor.scheduled_appointment_prefix),
    waitingAppointmentPrefix: readString(doctor.waiting_appointment_prefix),
    showQueueNumberToPatient: !!doctor.show_queue_number_to_patient,
    showRemainingTurnsToPatient: !!doctor.show_remaining_turns_to_patient,
    enableTurnApproachingReminder: !!doctor.enable_turn_approaching_reminder,
    reminderWhenRemainingTurns: String(doctor.reminder_when_remaining_turns ?? ''),
    showEstimatedTurnTimeToPatient: !!doctor.show_estimated_turn_time_to_patient,
    reminderBeforeMinutes: String(doctor.reminder_before_minutes ?? ''),
    reminderPatientBeforeMinutes: String(doctor.reminder_patient_before_minutes ?? ''),
    commissionRate: String(doctor.commission_rate ?? doctor.commission ?? ''),
    averageRating: Number(doctor.average_rating ?? 0),
    totalReviews: Number(doctor.total_reviews ?? 0),
    clinicId: readId(doctor.clinic?.id || doctor.clinic_id),
    hospitalId: readId(doctor.hospital?.id || doctor.hospital_id),
    clinicAddress:
      readString(doctor.address) ||
      readString(doctor.contact_address) ||
      readString(doctor.clinic_address) ||
      readString(doctor.clinic?.address) ||
      readString(doctor.dactrha_display_address),
    image: resolveDoctorProfileImage(doctor),
  };
};

const buildSpecialtyOptions = (response, lang) => {
  const payload = response?.data;
  const rows = firstArray(
    payload?.data?.specialities,
    payload?.specialities,
    payload?.data,
    payload
  );

  return rows
    .map((item) => ({
      value: String(item.id),
      label: getLocalizedValue(item.name, lang) || readString(item.name),
    }))
    .filter((item) => item.value && item.label);
};

const buildClinicOptions = (response) => {
  const payload = response?.data;
  const rows = firstArray(payload?.data?.clinics, payload?.clinics, payload?.data, payload);

  return rows
    .map((item) => ({
      value: String(item.id),
      label: readString(item.name),
    }))
    .filter((item) => item.value && item.label);
};

const buildHospitalOptions = (response) => {
  const payload = response?.data;
  const rows = firstArray(payload?.hospitals, payload?.data?.hospitals, payload?.data, payload);

  return rows
    .map((item) => ({
      value: String(item.id),
      label: readString(item.name),
    }))
    .filter((item) => item.value && item.label);
};

const DoctorProfile = () => {
  const { i18n } = useTranslation();
  const { user, isAuthenticated, updateUser } = useAuth();
  const navigate = useNavigate();
  const lang = (i18n.resolvedLanguage || i18n.language || 'en').toLowerCase().startsWith('ar') ? 'ar' : 'en';
  const isArabic = lang === 'ar';
  const imageInputRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [profile, setProfile] = useState(EMPTY_PROFILE);
  const [specialties, setSpecialties] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [hospitals, setHospitals] = useState([]);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordVisibility, setPasswordVisibility] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false,
  });

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    const run = async () => {
      setPageLoading(true);
      try {
        const [profileResponse, specialtiesResponse, clinicsResponse, hospitalsResponse] = await Promise.all([
          doctorService.getProfile(),
          specialtyService.getAll().catch(() => null),
          clinicService.getAllClinics({ limit: 200 }).catch(() => null),
          hospitalService.getAll({ limit: 200 }).catch(() => null),
        ]);

        const doctor = profileResponse?.data?.data || {};
        setProfile(mapDoctorToProfile(doctor, lang, user));
        setSpecialties(buildSpecialtyOptions(specialtiesResponse, lang));
        setClinics(buildClinicOptions(clinicsResponse));
        setHospitals(buildHospitalOptions(hospitalsResponse));
      } catch (error) {
        toast.error(isArabic ? 'فشل تحميل الملف الشخصي' : 'Failed to load profile', {
          description:
            error.response?.data?.message ||
            error.response?.data?.error ||
            (isArabic ? 'تعذر جلب بيانات الطبيب من الخادم' : 'Could not fetch doctor data from API'),
        });
      } finally {
        setPageLoading(false);
      }
    };

    run();
  }, [isAuthenticated, navigate, lang, isArabic, user]);

  const toggleSpecialty = (specialtyId, checked) => {
    setProfile((prev) => ({
      ...prev,
      specialtyIds: checked
        ? [...prev.specialtyIds, specialtyId].filter((value, index, array) => array.indexOf(value) === index)
        : prev.specialtyIds.filter((value) => value !== specialtyId),
    }));
  };

  const handleProfileImagePick = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error(isArabic ? 'يرجى اختيار صورة صحيحة' : 'Please select a valid image');
      event.target.value = '';
      return;
    }

    try {
      const bitmap = await createImageBitmap(file);
      const scale = Math.min(1, 1280 / Math.max(bitmap.width, bitmap.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(bitmap.width * scale));
      canvas.height = Math.max(1, Math.round(bitmap.height * scale));
      canvas.getContext('2d').drawImage(bitmap, 0, 0, canvas.width, canvas.height);
      bitmap.close();
      setProfile((prev) => ({ ...prev, image: canvas.toDataURL('image/jpeg', 0.82) }));
    } catch (_) {
      toast.error(isArabic ? 'فشل قراءة الصورة' : 'Failed to read image');
    }
    event.target.value = '';
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const sanitizedBio = stripHtml(profile.bio);
      const specialtyIds = profile.specialtyIds
        .map((value) => Number(value))
        .filter((value) => Number.isFinite(value) && value > 0);
      const primarySpecialtyId = specialtyIds[0] || false;
      const payload = {
        bio: sanitizedBio,
        description: sanitizedBio,
        clinic_address: profile.clinicAddress,
        address: profile.clinicAddress,
        license_number: profile.licenseNumber,
        experience_years: profile.experience ? Number(profile.experience) : 0,
        consultation_price: profile.consultationFee ? Number(profile.consultationFee) : 0,
        price: profile.consultationFee ? Number(profile.consultationFee) : 0,
        discount_price: profile.discountPrice ? Number(profile.discountPrice) : 0,
        doctor_currency: profile.doctorCurrency || 'SYP',
        currency: profile.doctorCurrency || 'SYP',
        currency_name: profile.doctorCurrency || 'SYP',
        priority: profile.priority ? Number(profile.priority) : 0,
        waiting_appointments_limit: profile.waitingAppointments ? Number(profile.waitingAppointments) : 0,
        waiting_appointments: profile.waitingAppointments ? Number(profile.waitingAppointments) : 0,
        featured: !!profile.featured,
        enable_appointment: !!profile.enableAppointment,
        accept_appointment: !!profile.acceptAppointment,
        allow_patient_multiple_bookings_same_day: !!profile.allowPatientMultipleBookingsSameDay,
        enable_waiting_appointments: !!profile.enableWaitingAppointments,
        waiting_appointment_delay: profile.waitingAppointmentDelay ? Number(profile.waitingAppointmentDelay) : 0,
        enable_online_consultation: !!profile.enableOnlineConsultation,
        enable_at_clinic: !!profile.enableAtClinic,
        enable_at_customer_address: !!profile.enableAtCustomerAddress,
        home_visit_fee: profile.homeVisitFee ? Number(profile.homeVisitFee) : 0,
        home_visit_discount_fee: profile.homeVisitDiscountFee ? Number(profile.homeVisitDiscountFee) : 0,
        session_duration: profile.sessionDuration ? Number(profile.sessionDuration) : 0,
        appointment_delay: profile.appointmentDelay ? Number(profile.appointmentDelay) : 0,
        scheduled_appointment_prefix: profile.scheduledAppointmentPrefix,
        waiting_appointment_prefix: profile.waitingAppointmentPrefix,
        show_queue_number_to_patient: !!profile.showQueueNumberToPatient,
        show_remaining_turns_to_patient: !!profile.showRemainingTurnsToPatient,
        enable_turn_approaching_reminder: !!profile.enableTurnApproachingReminder,
        reminder_when_remaining_turns: profile.reminderWhenRemainingTurns ? Number(profile.reminderWhenRemainingTurns) : 0,
        show_estimated_turn_time_to_patient: !!profile.showEstimatedTurnTimeToPatient,
        reminder_before_minutes: profile.reminderBeforeMinutes ? Number(profile.reminderBeforeMinutes) : 0,
        reminder_patient_before_minutes: profile.reminderPatientBeforeMinutes ? Number(profile.reminderPatientBeforeMinutes) : 0,
        clinic_id: profile.clinicId ? Number(profile.clinicId) : false,
        hospital_id: profile.hospitalId ? Number(profile.hospitalId) : false,
        speciality_ids: specialtyIds,
        specialty_ids: specialtyIds,
        specialty_id: primarySpecialtyId,
        speciality_id: primarySpecialtyId,
        ...(profile.image.startsWith('data:') ? { image: profile.image } : {}),
      };

      const response = await doctorService.updateProfile(profile.id || user?.id, payload);
      const savedDoctor = response?.data?.data || {};
      const savedImage = resolveDoctorProfileImage(savedDoctor) || normalizeImageValue(profile.image);
      const savedFirstName = readString(savedDoctor.first_name) || profile.firstName;
      const savedLastName = readString(savedDoctor.last_name) || profile.lastName;
      const savedFullName = joinNameParts(savedFirstName, savedLastName) || readString(savedDoctor.name) || profile.accountName;
      const syncedUser = {
        ...user,
        first_name: savedFirstName,
        last_name: savedLastName,
        name: savedFullName,
      };

      setProfile({
        ...mapDoctorToProfile(savedDoctor, lang, syncedUser),
        accountName: savedFullName,
        firstName: savedFirstName,
        lastName: savedLastName,
        image: savedImage,
      });
      updateUser({
        first_name: savedFirstName,
        last_name: savedLastName,
        email: savedDoctor.email ?? profile.email,
        phone_number: savedDoctor.phone_number ?? savedDoctor.phone ?? profile.phone,
        phone: savedDoctor.phone ?? savedDoctor.phone_number ?? profile.phone,
        name: savedFullName,
        ...(savedImage
          ? {
            image: savedImage,
            avatar: savedImage,
            profile_image: savedImage,
            profile_image_updated_at: Date.now(),
          }
          : {}),
      }, { persistAccountProfile: true });
      toast.success(isArabic ? 'تم حفظ الملف الشخصي' : 'Profile saved successfully');
    } catch (error) {
      toast.error(isArabic ? 'فشل حفظ الملف الشخصي' : 'Failed to save profile', {
        description:
          error.response?.data?.message ||
          error.response?.data?.error ||
          (isArabic ? 'تعذر تحديث بيانات الطبيب' : 'Could not update doctor profile'),
      });
    } finally {
      setLoading(false);
    }
  };

  const resetPasswordForm = () => {
    setPasswordData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    });
    setPasswordVisibility({
      currentPassword: false,
      newPassword: false,
      confirmPassword: false,
    });
  };

  const handlePasswordFieldChange = (event) => {
    const { name, value } = event.target;
    setPasswordData((prev) => ({ ...prev, [name]: value }));
  };

  const togglePasswordVisibility = (field) => {
    setPasswordVisibility((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const handlePasswordDialogChange = (open) => {
    setPasswordDialogOpen(open);
    if (!open && !passwordLoading) {
      resetPasswordForm();
    }
  };

  const handleChangePassword = async () => {
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      toast.error(isArabic ? 'يرجى تعبئة جميع حقول كلمة المرور' : 'Please fill in all password fields');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast.error(
        isArabic ? 'يجب أن تكون كلمة المرور الجديدة 6 أحرف على الأقل' : 'New password must be at least 6 characters'
      );
      return;
    }

    if (passwordData.currentPassword === passwordData.newPassword) {
      toast.error(
        isArabic ? 'لا يمكن أن تكون كلمة المرور الجديدة مطابقة لكلمة المرور الحالية' : 'New password must be different from current password'
      );
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error(
        isArabic ? 'كلمة المرور الجديدة وتأكيدها غير متطابقين' : 'New password and confirmation do not match'
      );
      return;
    }

    setPasswordLoading(true);
    try {
      await authService.changePassword(passwordData.currentPassword, passwordData.newPassword);
      toast.success(isArabic ? 'تم تغيير كلمة المرور بنجاح' : 'Password changed successfully');
      setPasswordDialogOpen(false);
      resetPasswordForm();
    } catch (error) {
      toast.error(isArabic ? 'فشل تغيير كلمة المرور' : 'Failed to change password', {
        description:
          error.response?.data?.message ||
          error.response?.data?.error ||
          (isArabic ? 'تحقق من كلمة المرور الحالية ثم أعد المحاولة' : 'Check your current password and try again'),
      });
    } finally {
      setPasswordLoading(false);
    }
  };

  if (!isAuthenticated) return null;

  if (pageLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 dark:bg-slate-900">
        <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  const selectedSpecialties = specialties.filter((item) => profile.specialtyIds.includes(item.value));
  const selectedSpecialtySummary = (
    selectedSpecialties.length > 0
      ? selectedSpecialties.map((item) => item.label)
      : profile.specialtyLabels
  ).join(' - ');

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900" data-testid="doctor-profile-page">
      <div className="border-b bg-white dark:border-slate-700 dark:bg-slate-800">
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate('/doctor/dashboard')}>
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
                  {isArabic ? 'الملف الشخصي' : 'Profile Settings'}
                </h1>
                <p className="text-slate-500 dark:text-slate-400">
                  {isArabic ? 'إدارة البيانات الشخصية والمهنية للطبيب' : 'Manage personal and professional doctor data'}
                </p>
              </div>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row">
              <Button
                variant="outline"
                className="border-slate-700 bg-slate-900/70 text-white hover:bg-slate-800 hover:text-white"
                onClick={() => setPasswordDialogOpen(true)}
              >
                <Lock className="mr-2 h-4 w-4" />
                {isArabic ? 'تغيير كلمة السر' : 'Change Password'}
              </Button>
              <Button className="bg-teal-600 hover:bg-teal-700" onClick={handleSave} disabled={loading}>
                {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                {isArabic ? 'حفظ' : 'Save'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto max-w-5xl space-y-6 px-4 py-6">
        <Card className="border-0 shadow-sm dark:bg-slate-800">
          <CardContent className="p-6">
            <div className="flex flex-col gap-6 md:flex-row md:items-center">
              <div className="relative">
                <Avatar className="h-24 w-24">
                  <AvatarImage src={profile.image || user?.avatar} />
                  <AvatarFallback className="bg-teal-600 text-2xl text-white">
                    {profile.firstName?.charAt(0)}
                    {profile.lastName?.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <Button
                  size="icon"
                  className="absolute -bottom-2 -right-2 h-8 w-8 rounded-full bg-teal-600 hover:bg-teal-700"
                  onClick={() => imageInputRef.current?.click()}
                >
                  <Camera className="h-4 w-4" />
                </Button>
                <input
                  ref={imageInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleProfileImagePick}
                />
              </div>

              <div className="space-y-2">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                    Dr. {[profile.firstName, profile.lastName].filter(Boolean).join(' ') || profile.accountName}
                  </h3>
                  <p className="text-slate-500 dark:text-slate-400">
                    {selectedSpecialtySummary || (isArabic ? 'بدون تخصص محدد' : 'No specialty selected')}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {profile.accountName && (
                    <Badge variant="secondary" className="bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-100">
                      {isArabic ? 'الحساب:' : 'Account:'} {profile.accountName}
                    </Badge>
                  )}
                  {profile.featured && (
                    <Badge className="bg-amber-500 text-white hover:bg-amber-500">
                      <ShieldCheck className="mr-1 h-3.5 w-3.5" />
                      {isArabic ? 'مميز' : 'Featured'}
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Tabs defaultValue="profile" className="w-full">
          <TabsList className="mb-6 h-auto w-full justify-start gap-2 overflow-x-auto rounded-2xl bg-slate-100 p-2 dark:bg-slate-800">
            <TabsTrigger value="profile">{isArabic ? 'حساب تعريفي' : 'Profile'}</TabsTrigger>
            <TabsTrigger value="availability">{isArabic ? 'التوفر' : 'Availability'}</TabsTrigger>
            <TabsTrigger value="billing">{isArabic ? 'الفواتير والتنبيهات' : 'Billing & Alerts'}</TabsTrigger>
            <TabsTrigger value="ratings">{isArabic ? 'التقييمات' : 'Ratings'}</TabsTrigger>
          </TabsList>

          <TabsContent value="profile" className="space-y-6">
            <Card className="border-0 shadow-sm dark:bg-slate-800">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <User className="h-5 w-5" />
                  {isArabic ? 'المعلومات الشخصية' : 'Personal Information'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <Label>{isArabic ? 'الاسم الأول' : 'First Name'}</Label>
                    <Input value={profile.firstName} disabled />
                  </div>
                  <div>
                    <Label>{isArabic ? 'الاسم الأخير' : 'Last Name'}</Label>
                    <Input value={profile.lastName} disabled />
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <Label>{isArabic ? 'البريد الإلكتروني' : 'Email'}</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                      <Input
                        value={profile.email}
                        disabled
                        className="pl-10"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>{isArabic ? 'اسم الحساب' : 'Account Name'}</Label>
                    <Input value={profile.accountName} disabled />
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <Label>{isArabic ? 'رقم الهاتف' : 'Phone Number'}</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                      <Input
                        value={profile.phone}
                        disabled
                        className="pl-10"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>{isArabic ? 'رقم الهاتف المحمول' : 'Mobile Number'}</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                      <Input
                        value={profile.mobile}
                        disabled
                        className="pl-10"
                      />
                    </div>
                  </div>
                </div>
                <p className="text-xs text-slate-500">
                  {isArabic
                    ? 'يتم تعديل الاسم والبريد والهاتف عن طريق إدارة النظام فقط.'
                    : 'Name, email, and phone can only be changed by administration.'}
                </p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm dark:bg-slate-800">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Briefcase className="h-5 w-5" />
                  {isArabic ? 'المعلومات المهنية' : 'Professional Information'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-5">
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label>{isArabic ? 'رقم الترخيص' : 'License Number'}</Label>
                <Input
                  value={profile.licenseNumber}
                  onChange={(event) => setProfile({ ...profile, licenseNumber: event.target.value })}
                />
              </div>
              <div>
                <Label>{isArabic ? 'سنوات الخبرة' : 'Years of Experience'}</Label>
                <Input
                  type="number"
                  min="0"
                  value={profile.experience}
                  onChange={(event) => setProfile({ ...profile, experience: event.target.value })}
                />
              </div>
            </div>

            <div>
              <div className="mb-2 flex items-center justify-between">
                <Label>{isArabic ? 'التخصصات' : 'Specialties'}</Label>
                <span className="text-xs text-slate-500 dark:text-slate-400">
                  {isArabic ? 'يمكن اختيار أكثر من تخصص' : 'You can choose more than one specialty'}
                </span>
              </div>

              {selectedSpecialties.length > 0 && (
                <div className="mb-3 flex flex-wrap gap-2">
                  {(selectedSpecialties.length > 0
                    ? selectedSpecialties.map((specialty) => specialty.label)
                    : profile.specialtyLabels
                  ).map((label, index) => (
                    <Badge key={`${label}-${index}`} variant="secondary">
                      {label}
                    </Badge>
                  ))}
                </div>
              )}

              <div className="grid gap-3 rounded-2xl border border-slate-200 p-4 dark:border-slate-700 md:grid-cols-2">
                {specialties.map((specialty) => {
                  const checked = profile.specialtyIds.includes(specialty.value);
                  return (
                    <label
                      key={specialty.value}
                      className="flex cursor-pointer items-center gap-3 rounded-xl border border-slate-200 px-3 py-2 text-sm transition hover:border-teal-400 dark:border-slate-700 dark:hover:border-teal-500"
                    >
                      <Checkbox
                        checked={checked}
                        onCheckedChange={(value) => toggleSpecialty(specialty.value, !!value)}
                      />
                      <span>{specialty.label}</span>
                    </label>
                  );
                })}

                {specialties.length === 0 && (
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    {isArabic ? 'لا توجد تخصصات متاحة حاليًا' : 'No specialties are available right now'}
                  </p>
                )}
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label>{isArabic ? 'العيادة' : 'Clinic'}</Label>
                <Select
                  value={toOptionValue(profile.clinicId)}
                  onValueChange={(value) => setProfile({ ...profile, clinicId: value === NONE_VALUE ? '' : value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={isArabic ? 'اختر العيادة' : 'Select clinic'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE_VALUE}>{isArabic ? 'بدون' : 'None'}</SelectItem>
                    {clinics.map((clinic) => (
                      <SelectItem key={clinic.value} value={clinic.value}>
                        {clinic.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>{isArabic ? 'المستشفى' : 'Hospital'}</Label>
                <Select
                  value={toOptionValue(profile.hospitalId)}
                  onValueChange={(value) => setProfile({ ...profile, hospitalId: value === NONE_VALUE ? '' : value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={isArabic ? 'اختر المستشفى' : 'Select hospital'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE_VALUE}>{isArabic ? 'بدون' : 'None'}</SelectItem>
                    {hospitals.map((hospital) => (
                      <SelectItem key={hospital.value} value={hospital.value}>
                        {hospital.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              <div>
                <Label>{isArabic ? 'سعر الاستشارة' : 'Consultation Fee'}</Label>
                <Input
                  type="number"
                  min="0"
                  value={profile.consultationFee}
                  onChange={(event) => setProfile({ ...profile, consultationFee: event.target.value })}
                />
              </div>
              <div>
                <Label>{isArabic ? 'السعر المخفض' : 'Discount Price'}</Label>
                <Input
                  type="number"
                  min="0"
                  value={profile.discountPrice}
                  onChange={(event) => setProfile({ ...profile, discountPrice: event.target.value })}
                />
              </div>
              <div>
                <Label>{isArabic ? 'العملة' : 'Currency'}</Label>
                <Select
                  value={profile.doctorCurrency || 'SYP'}
                  onValueChange={(value) => setProfile({ ...profile, doctorCurrency: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={isArabic ? 'اختر العملة' : 'Select currency'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="SYP">{isArabic ? 'ليرة سورية' : 'Syrian Pound'} (SYP)</SelectItem>
                    <SelectItem value="USD">{isArabic ? 'دولار أمريكي' : 'US Dollar'} (USD)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label>{isArabic ? 'الأولوية' : 'Priority'}</Label>
                <Input
                  type="number"
                  min="0"
                  value={profile.priority}
                  onChange={(event) => setProfile({ ...profile, priority: event.target.value })}
                />
              </div>
              <div>
                <Label>{isArabic ? 'حد انتظار المواعيد اليومي' : 'Daily Waiting Appointments Limit'}</Label>
                <Input
                  type="number"
                  min="0"
                  value={profile.waitingAppointments}
                  onChange={(event) => setProfile({ ...profile, waitingAppointments: event.target.value })}
                />
              </div>
            </div>

            <div className="flex items-center justify-between rounded-2xl border border-slate-200 p-4 dark:border-slate-700">
              <div className="space-y-1">
                <Label className="text-base">{isArabic ? 'طبيب مميز' : 'Featured Doctor'}</Label>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {isArabic
                    ? 'إظهار الطبيب ضمن الأطباء المميزين داخل التطبيق'
                    : 'Show this doctor in the featured doctors surfaces'}
                </p>
              </div>
              <Switch
                dir={isArabic ? 'rtl' : 'ltr'}
                checked={profile.featured}
                onCheckedChange={(checked) => setProfile({ ...profile, featured: checked })}
              />
            </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm dark:bg-slate-800">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <MapPin className="h-5 w-5" />
                  {isArabic ? 'معلومات إضافية' : 'Additional Information'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>{isArabic ? 'عنوان العيادة / مكان العمل' : 'Clinic / Practice Address'}</Label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                    <Textarea
                      rows={3}
                      value={profile.clinicAddress}
                      onChange={(event) => setProfile({ ...profile, clinicAddress: event.target.value })}
                      className="pl-10"
                    />
                  </div>
                </div>

                <div>
                  <Label>{isArabic ? 'نبذة عن الطبيب' : 'Doctor Bio'}</Label>
                  <Textarea
                    rows={4}
                    value={profile.bio}
                    onChange={(event) => setProfile({ ...profile, bio: event.target.value })}
                    placeholder={
                      isArabic ? 'اكتب نبذة مختصرة عن الطبيب وخبرته وخدماته...' : 'Write a short bio about the doctor, experience, and services...'
                    }
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="availability" className="space-y-6">
            <Card className="border-0 shadow-sm dark:bg-slate-800">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <HeartHandshake className="h-5 w-5" />
                  {isArabic ? 'إعدادات التوفر والمواعيد' : 'Availability & Appointment Settings'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid gap-6 md:grid-cols-2">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between rounded-2xl border border-slate-200 p-4 dark:border-slate-700">
                      <Label>{isArabic ? 'تمكين المواعيد' : 'Enable Appointments'}</Label>
                      <Switch checked={profile.enableAppointment} onCheckedChange={(checked) => setProfile({ ...profile, enableAppointment: checked })} />
                    </div>
                    <div className="flex items-center justify-between rounded-2xl border border-slate-200 p-4 dark:border-slate-700">
                      <Label>{isArabic ? 'قبول الموعد' : 'Auto Accept Appointments'}</Label>
                      <Switch checked={profile.acceptAppointment} onCheckedChange={(checked) => setProfile({ ...profile, acceptAppointment: checked })} />
                    </div>
                    <div className="flex items-center justify-between rounded-2xl border border-slate-200 p-4 dark:border-slate-700">
                      <Label>{isArabic ? 'السماح للمريض بالحجز أكثر من مرة في اليوم نفسه' : 'Allow same-day multiple bookings'}</Label>
                      <Switch
                        checked={profile.allowPatientMultipleBookingsSameDay}
                        onCheckedChange={(checked) => setProfile({ ...profile, allowPatientMultipleBookingsSameDay: checked })}
                      />
                    </div>
                    <div className="flex items-center justify-between rounded-2xl border border-slate-200 p-4 dark:border-slate-700">
                      <Label>{isArabic ? 'تمكين انتظار المواعيد' : 'Enable Waiting Appointments'}</Label>
                      <Switch
                        checked={profile.enableWaitingAppointments}
                        onCheckedChange={(checked) => setProfile({ ...profile, enableWaitingAppointments: checked })}
                      />
                    </div>
                    <div>
                      <Label>{isArabic ? 'تأخير موعد الانتظار بالدقائق' : 'Waiting Appointment Delay (minutes)'}</Label>
                      <Input
                        type="number"
                        min="0"
                        value={profile.waitingAppointmentDelay}
                        onChange={(event) => setProfile({ ...profile, waitingAppointmentDelay: event.target.value })}
                      />
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center justify-between rounded-2xl border border-slate-200 p-4 dark:border-slate-700">
                      <Label>{isArabic ? 'تمكين الاستشارة عبر الإنترنت' : 'Enable Online Consultation'}</Label>
                      <Switch
                        checked={profile.enableOnlineConsultation}
                        onCheckedChange={(checked) => setProfile({ ...profile, enableOnlineConsultation: checked })}
                      />
                    </div>
                    <div className="flex items-center justify-between rounded-2xl border border-slate-200 p-4 dark:border-slate-700">
                      <Label>{isArabic ? 'تمكين في العيادة' : 'Enable At Clinic'}</Label>
                      <Switch checked={profile.enableAtClinic} onCheckedChange={(checked) => setProfile({ ...profile, enableAtClinic: checked })} />
                    </div>
                    <div className="flex items-center justify-between rounded-2xl border border-slate-200 p-4 dark:border-slate-700">
                      <Label>{isArabic ? 'تمكين في عنوان العميل' : 'Enable At Customer Address'}</Label>
                      <Switch
                        checked={profile.enableAtCustomerAddress}
                        onCheckedChange={(checked) => setProfile({ ...profile, enableAtCustomerAddress: checked })}
                      />
                    </div>
                    {profile.enableAtCustomerAddress && (
                      <div className="grid gap-4 md:grid-cols-2">
                        <div>
                          <Label>{isArabic ? 'رسم الزيارة المنزلية' : 'Home visit fee'}</Label>
                          <Input type="number" min="0" value={profile.homeVisitFee} onChange={(event) => setProfile({ ...profile, homeVisitFee: event.target.value })} />
                        </div>
                        <div>
                          <Label>{isArabic ? 'رسم الزيارة بعد الخصم' : 'Discounted home visit fee'}</Label>
                          <Input type="number" min="0" value={profile.homeVisitDiscountFee} onChange={(event) => setProfile({ ...profile, homeVisitDiscountFee: event.target.value })} />
                        </div>
                      </div>
                    )}
                    <div>
                      <Label>{isArabic ? 'مدة الجلسة بالدقائق' : 'Session Duration (minutes)'}</Label>
                      <Input
                        type="number"
                        min="0"
                        value={profile.sessionDuration}
                        onChange={(event) => setProfile({ ...profile, sessionDuration: event.target.value })}
                      />
                    </div>
                    <div>
                      <Label>{isArabic ? 'تأخير الموعد بالدقائق' : 'Appointment Delay (minutes)'}</Label>
                      <Input
                        type="number"
                        min="0"
                        value={profile.appointmentDelay}
                        onChange={(event) => setProfile({ ...profile, appointmentDelay: event.target.value })}
                      />
                    </div>
                    <div>
                      <Label>{isArabic ? 'الحد اليومي لمواعيد الانتظار' : 'Daily Waiting Appointments Limit'}</Label>
                      <Input
                        type="number"
                        min="0"
                        value={profile.waitingAppointments}
                        onChange={(event) => setProfile({ ...profile, waitingAppointments: event.target.value })}
                      />
                    </div>
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <Label>{isArabic ? 'بادئة الموعد المجدول' : 'Scheduled Appointment Prefix'}</Label>
                    <Input
                      value={profile.scheduledAppointmentPrefix}
                      onChange={(event) => setProfile({ ...profile, scheduledAppointmentPrefix: event.target.value })}
                    />
                  </div>
                  <div>
                    <Label>{isArabic ? 'بادئة موعد الانتظار' : 'Waiting Appointment Prefix'}</Label>
                    <Input
                      value={profile.waitingAppointmentPrefix}
                      onChange={(event) => setProfile({ ...profile, waitingAppointmentPrefix: event.target.value })}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm dark:bg-slate-800">
              <CardHeader>
                <CardTitle className="text-lg">{isArabic ? 'إظهار معلومات الدور' : 'Queue Visibility'}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between rounded-2xl border border-slate-200 p-4 dark:border-slate-700">
                  <Label>{isArabic ? 'إظهار رقم الدور للمريض' : 'Show Queue Number To Patient'}</Label>
                  <Switch
                    checked={profile.showQueueNumberToPatient}
                    onCheckedChange={(checked) => setProfile({ ...profile, showQueueNumberToPatient: checked })}
                  />
                </div>
                <div className="flex items-center justify-between rounded-2xl border border-slate-200 p-4 dark:border-slate-700">
                  <Label>{isArabic ? 'إظهار الأدوار المتبقية للمريض' : 'Show Remaining Turns To Patient'}</Label>
                  <Switch
                    checked={profile.showRemainingTurnsToPatient}
                    onCheckedChange={(checked) => setProfile({ ...profile, showRemainingTurnsToPatient: checked })}
                  />
                </div>
                <div className="flex items-center justify-between rounded-2xl border border-slate-200 p-4 dark:border-slate-700">
                  <Label>{isArabic ? 'تفعيل تذكير اقتراب الدور' : 'Enable Turn-Approaching Reminder'}</Label>
                  <Switch
                    checked={profile.enableTurnApproachingReminder}
                    onCheckedChange={(checked) => setProfile({ ...profile, enableTurnApproachingReminder: checked })}
                  />
                </div>
                <div>
                  <Label>{isArabic ? 'إرسال التذكير عندما يصبح عدد الأدوار المتبقية' : 'Send Reminder When Remaining Turns Are'}</Label>
                  <Input
                    type="number"
                    min="0"
                    value={profile.reminderWhenRemainingTurns}
                    onChange={(event) => setProfile({ ...profile, reminderWhenRemainingTurns: event.target.value })}
                  />
                </div>
                <div className="flex items-center justify-between rounded-2xl border border-slate-200 p-4 dark:border-slate-700">
                  <Label>{isArabic ? 'إظهار الوقت التقديري للمريض' : 'Show Estimated Turn Time To Patient'}</Label>
                  <Switch
                    checked={profile.showEstimatedTurnTimeToPatient}
                    onCheckedChange={(checked) => setProfile({ ...profile, showEstimatedTurnTimeToPatient: checked })}
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="billing" className="space-y-6">
            <Card className="border-0 shadow-sm dark:bg-slate-800">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <BellRing className="h-5 w-5" />
                  {isArabic ? 'الفواتير والتنبيهات' : 'Billing & Alerts'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <Label>{isArabic ? 'نسبة العمولة' : 'Commission Rate'}</Label>
                    <Input value={profile.commissionRate} disabled />
                  </div>
                  <div>
                    <Label>{isArabic ? 'تذكير قبل الموعد بالدقائق' : 'Reminder Before Minutes'}</Label>
                    <Input
                      type="number"
                      min="0"
                      value={profile.reminderBeforeMinutes}
                      onChange={(event) => setProfile({ ...profile, reminderBeforeMinutes: event.target.value })}
                    />
                  </div>
                </div>
                <div>
                  <Label>{isArabic ? 'تذكير المريض قبل الموعد بالدقائق' : 'Patient Reminder Before Minutes'}</Label>
                  <Input
                    type="number"
                    min="0"
                    value={profile.reminderPatientBeforeMinutes}
                    onChange={(event) => setProfile({ ...profile, reminderPatientBeforeMinutes: event.target.value })}
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="ratings" className="space-y-6">
            <Card className="border-0 shadow-sm dark:bg-slate-800">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Star className="h-5 w-5" />
                  {isArabic ? 'التقييمات' : 'Ratings'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="rounded-2xl border border-slate-200 p-4 text-center dark:border-slate-700">
                    <div className="text-3xl font-bold text-slate-900 dark:text-white">{profile.averageRating.toFixed(1)}</div>
                    <div className="text-sm text-slate-500 dark:text-slate-400">{isArabic ? 'متوسط التقييم' : 'Average Rating'}</div>
                  </div>
                  <div className="rounded-2xl border border-slate-200 p-4 text-center dark:border-slate-700">
                    <div className="text-3xl font-bold text-slate-900 dark:text-white">{profile.totalReviews}</div>
                    <div className="text-sm text-slate-500 dark:text-slate-400">{isArabic ? 'عدد التقييمات' : 'Total Reviews'}</div>
                  </div>
                </div>
                <Button variant="outline" onClick={() => navigate('/doctor/reviews')}>
                  <Star className="mr-2 h-4 w-4" />
                  {isArabic ? 'فتح صفحة التقييمات' : 'Open Reviews Page'}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={passwordDialogOpen} onOpenChange={handlePasswordDialogChange}>
        <DialogContent className="border border-slate-200 bg-white text-slate-900 shadow-2xl dark:border-slate-700 dark:bg-slate-900 dark:text-white sm:max-w-xl">
          <DialogHeader className="space-y-3 text-center sm:text-center">
            <DialogTitle className="text-3xl font-bold text-slate-900 dark:text-white">
              {isArabic ? 'تغيير كلمة السر' : 'Change Password'}
            </DialogTitle>
            <DialogDescription className="mx-auto max-w-md text-base leading-7 text-slate-500 dark:text-slate-300">
              {isArabic
                ? 'أدخل كلمة المرور الحالية ثم كلمة المرور الجديدة للحفاظ على أمان حسابك.'
                : 'Enter your current password and choose a new one to keep your account secure.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 rounded-[28px] bg-slate-50 p-4 text-slate-900 dark:bg-slate-800/70 dark:text-slate-100 sm:p-6">
            {[
              {
                key: 'currentPassword',
                label: isArabic ? 'كلمة المرور القديمة' : 'Current Password',
              },
              {
                key: 'newPassword',
                label: isArabic ? 'كلمة مرور جديدة' : 'New Password',
              },
              {
                key: 'confirmPassword',
                label: isArabic ? 'تأكيد كلمة المرور الجديدة' : 'Confirm New Password',
              },
            ].map((field) => (
              <div
                key={field.key}
                className="rounded-3xl border border-slate-200 bg-white px-4 py-3 shadow-sm dark:border-slate-700 dark:bg-slate-900/80"
              >
                <Label htmlFor={field.key} className="mb-2 block text-sm font-semibold text-slate-700 dark:text-slate-200">
                  {field.label}
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
                  <Input
                    id={field.key}
                    name={field.key}
                    type={passwordVisibility[field.key] ? 'text' : 'password'}
                    value={passwordData[field.key]}
                    onChange={handlePasswordFieldChange}
                    className="h-12 rounded-2xl border-slate-200 bg-slate-50 pl-10 pr-12 text-base text-slate-900 focus-visible:ring-teal-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility(field.key)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 transition hover:text-slate-700 dark:text-slate-500 dark:hover:text-slate-200"
                    aria-label={passwordVisibility[field.key] ? 'Hide password' : 'Show password'}
                  >
                    {passwordVisibility[field.key] ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>
            ))}
          </div>

          <DialogFooter className="flex-col gap-3 sm:flex-row sm:justify-center sm:space-x-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => handlePasswordDialogChange(false)}
              disabled={passwordLoading}
              className="w-full border-slate-200 bg-white text-slate-700 hover:bg-slate-100 hover:text-slate-900 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700 dark:hover:text-white sm:w-40"
            >
              {isArabic ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button
              type="button"
              onClick={handleChangePassword}
              disabled={passwordLoading}
              className="w-full bg-teal-600 text-white hover:bg-teal-700 sm:w-48"
            >
              {passwordLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Lock className="mr-2 h-4 w-4" />
                  {isArabic ? 'حفظ' : 'Save'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DoctorProfile;
